import { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Button,
  Grid,
  Box,
  CardContent,
  Typography,
  MenuList,
  ListItem,
  ListItemProps,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { IconButton } from "@mui/material";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { IDispatchDeskExpanded } from "src/redux/reducers/DispatchDeskReducer";
import { DispatchDeskForm } from "./DispatchDeskForm";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { useDispatch } from "react-redux";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IManageDispatchDesksProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  height: number;
}

// ---------------------------------------------------------------------------
// Styled sidebar list item
// ---------------------------------------------------------------------------

const DeskListItem: React.FC<ListItemProps & { deskColor?: string }> = styled(
  ({ deskColor, ...rest }: ListItemProps & { deskColor?: string }) => <ListItem {...rest} />
)(({ theme, deskColor }) => ({
  color: theme.palette.common.white,
  padding: "8px 12px",
  margin: "2px",
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderLeft: deskColor ? `4px solid ${deskColor}` : "4px solid transparent",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ManageDispatchDesks = (props: IManageDispatchDesksProps): JSX.Element => {
  const { setActiveModal, height } = props;
  const { cancellablePromise } = useCancellablePromise();
  const reduxDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isReordering, setIsReordering] = useState<boolean>(false);
  const [desks, setDesks] = useState<IDispatchDeskExpanded[]>([]);
  const [selectedDesk, setSelectedDesk] = useState<IDispatchDeskExpanded | undefined>(undefined);

  // ------ Fetch desks on mount ------

  useEffect(() => {
    const fetchDesks = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IDispatchDeskExpanded[]>(
        "/dispatchdesks",
        undefined,
        cancellablePromise
      );
      setIsLoading(false);
      if (Array.isArray(response)) {
        setDesks(response.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)));
      }
    };
    fetchDesks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------ Update desk list after CRUD ------

  const updateDeskList = useCallback(
    (updatedDesks: IDispatchDeskExpanded[], mode: "create" | "update" | "delete", deskId?: string): void => {
      if (Array.isArray(updatedDesks) && updatedDesks.length > 0) {
        // API returns the full list of desks – use it directly
        const sorted = [...updatedDesks].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
        setDesks(sorted);

        if (mode === "delete") {
          setSelectedDesk(undefined);
        } else if (mode === "create") {
          // Select the newly created desk
          const prevIds = desks.map((d) => d._id);
          const newDesk = sorted.find((d) => !prevIds.includes(d._id)) ?? sorted[sorted.length - 1];
          setSelectedDesk(newDesk);
        } else if (mode === "update" && deskId) {
          const updated = sorted.find((d) => d._id === deskId);
          if (updated) setSelectedDesk(updated);
        }
      } else if (mode === "delete") {
        // Fallback: remove locally
        setDesks((prev) => prev.filter((d) => d._id !== deskId));
        setSelectedDesk(undefined);
      }
    },
    [desks]
  );

  // ------ Reorder desks (up/down arrows) ------

  const handleReorder = useCallback(
    async (index: number, direction: "up" | "down"): Promise<void> => {
      const swapIndex = direction === "up" ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= desks.length) return;

      setIsReordering(true);
      const deskA = desks[index];
      const deskB = desks[swapIndex];

      // Assign sequential orders based on swapped positions
      const orderA = swapIndex;
      const orderB = index;

      const notifications: INotifications = {
        dispatch: reduxDispatch,
        errorMessage: "Failed to reorder desks.",
      };

      try {
        // Update the first desk
        const responseA = await AmplifyAPIWrapper.Put<any>(
          `/admin/dispatchdesk/${deskA._id}`,
          { displayOrder: orderA },
          undefined
        );
        // Update the second desk
        const responseB = await AmplifyAPIWrapper.Put<any>(
          `/admin/dispatchdesk/${deskB._id}`,
          { displayOrder: orderB },
          notifications
        );

        // Use the latest response (it returns all desks)
        const latest = Array.isArray(responseB) ? responseB : Array.isArray(responseA) ? responseA : null;
        if (latest) {
          const sorted = [...latest].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
          setDesks(sorted);
          // Keep selected desk in sync
          if (selectedDesk) {
            const updated = sorted.find((d) => d._id === selectedDesk._id);
            if (updated) setSelectedDesk(updated);
          }
        }
      } catch {
        // silent – notification handles error
      }
      setIsReordering(false);
    },
    [desks, reduxDispatch, selectedDesk]
  );

  // ------ Render ------

  return (
    <CardContent>
      <Grid container spacing={3} direction="row">
        {/* ---------- Left sidebar: desk list ---------- */}
        <Grid item xs={3}>
          <Button
            variant="contained"
            color="success"
            size="small"
            fullWidth
            onClick={() => setSelectedDesk(undefined)}
            sx={{ mb: 3 }}
          >
            <AddIcon /> Add New Desk
          </Button>
          <Paper sx={{ height: `calc(${height}px - 100px)`, overflowY: "scroll", overflowX: "hidden" }}>
            {isLoading ? (
              <Box sx={{ p: 2 }}>
                <LoadingWrapper message="Loading Dispatch Desks" />
              </Box>
            ) : desks.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  No dispatch desks configured. Click "Add New Desk" to create one.
                </Typography>
              </Box>
            ) : (
              <MenuList>
                {desks.map((desk, idx) => (
                  <DeskListItem
                    key={desk._id}
                    deskColor={desk.color}
                    selected={selectedDesk?._id === desk._id}
                    onClick={() => setSelectedDesk(desk)}
                    sx={{
                      bgcolor: selectedDesk?._id === desk._id ? "primary.dark" : "transparent",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: selectedDesk?._id === desk._id ? 700 : 400 }}>
                        {desk.name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.5 }}>
                        {desk.users.length} user{desk.users.length !== 1 ? "s" : ""} · {desk.flights.length} flight
                        {desk.flights.length !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
                      <IconButton
                        size="small"
                        disabled={idx === 0 || isReordering}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReorder(idx, "up");
                        }}
                        sx={{ p: 0, opacity: idx === 0 ? 0.2 : 0.7 }}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={idx === desks.length - 1 || isReordering}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReorder(idx, "down");
                        }}
                        sx={{ p: 0, opacity: idx === desks.length - 1 ? 0.2 : 0.7 }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </DeskListItem>
                ))}
              </MenuList>
            )}
          </Paper>
        </Grid>

        {/* ---------- Right side: form ---------- */}
        <Grid item xs>
          <Typography variant="h5">
            {selectedDesk ? `Edit: ${selectedDesk.name}` : "Create New Dispatch Desk"}
          </Typography>

          <Grid container item>
            <Grid item xs={6}>
              <Alert severity="info" variant="filled" sx={{ mt: 1 }}>
                {selectedDesk
                  ? "Update the desk properties below. Flight and user assignments are managed from the Dispatch Desk window."
                  : "Create a new dispatch desk by filling in the details below."}
              </Alert>

              <DispatchDeskForm
                selectedDesk={selectedDesk}
                updateDeskList={updateDeskList}
                setActiveModal={setActiveModal}
                setSelectedDesk={setSelectedDesk}
                nextDisplayOrder={desks.length}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};
